import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router"

import { Button } from "components/Button"
import { Buttons, Card } from "components/Card/Card"
import { UiCarousel } from "components/Carousel/Index"
import PreviousButton from "components/Previous/PreviousButton"
import { UiQuestionsLine } from "components/Question/Question"
import { Slider } from "components/Slider/Slider"
import { PATH } from "containers/Survey/models"
import {
  getPatchAnswerValues,
  getPatchFollowup,
  getRepliesForValues,
  getStaleFollowupAnswerIds,
} from "containers/Survey/patch_models"
import actions from "containers/Survey/services/actions"
import { Question, Reply, Value } from "containers/Survey/types"
import {
  findQuestionById,
  findQuestionsLineParent,
  isValueSelected,
  valueEquals,
} from "containers/Survey/utils"
import { DERMATO_RECAP_PAGE } from "core/constants"
import { useLanguage } from "locales"
import { SurveyStore } from "types/store"

import styles from "./SurveyPatch.module.scss"

type QuestionsLine = Question & { questions: Question[] }

/**
 * Modification d'une réponse déjà donnée, depuis le résumé de la demande.
 * Une seule question par écran : la question ciblée (pré-remplie) puis, si le
 * choix l'ouvre, sa sous-question. Rien n'est écrit avant "Enregistrer".
 */
const SurveyPatch: React.FC = () => {
  const { questionId } = useParams()
  const { state } = useLocation() as {
    state?: { step?: number; editableSteps?: number[] }
  }
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { languages } = useLanguage()
  const { answers, questions } = useSelector(
    ({ survey }: { survey: SurveyStore }) => survey,
  )

  const targetId = questionId ? decodeURIComponent(questionId) : ""
  const question = useMemo(
    () => findQuestionById(targetId, questions),
    [targetId, questions],
  )
  const lineParent = useMemo(
    () => findQuestionsLineParent(targetId, questions),
    [targetId, questions],
  )

  const [values, setValues] = useState<Value[]>(() =>
    getPatchAnswerValues(answers, targetId, lineParent?.id),
  )
  const [followupDrafts, setFollowupDrafts] = useState<Record<string, Value[]>>(
    {},
  )
  const [onFollowup, setOnFollowup] = useState(false)

  useEffect(() => {
    if (!question) navigate(DERMATO_RECAP_PAGE, { replace: true })
  }, [question, navigate])

  if (!question) return null

  const followup = getPatchFollowup(question, values)
  const followupValues = followup
    ? followupDrafts[followup.answerId] ??
      getPatchAnswerValues(answers, followup.answerId)
    : []

  const isFollowupStep = Boolean(followup) && onFollowup
  const displayed = isFollowupStep && followup ? followup.question : question
  const displayedValues = isFollowupStep ? followupValues : values

  const backToRecap = () =>
    navigate(DERMATO_RECAP_PAGE, {
      state: { step: state?.step ?? 0, editableSteps: state?.editableSteps },
    })

  const previous = () => {
    if (isFollowupStep) {
      setOnFollowup(false)
      return
    }
    backToRecap()
  }

  const onChange = (next: Value[]) => {
    if (isFollowupStep && followup) {
      setFollowupDrafts((drafts) => ({ ...drafts, [followup.answerId]: next }))
      return
    }
    setValues(next)
    // Un choix unique qui ouvre une sous-question enchaîne directement dessus,
    // comme dans le questionnaire.
    setOnFollowup(
      !question.multipleReplies && Boolean(getPatchFollowup(question, next)),
    )
  }

  const save = () => {
    if (lineParent) {
      // La réponse d'une question_line vit dans la réponse de son parent :
      // le reducer remplace l'entrée de même `title` sans toucher aux autres.
      dispatch(
        actions.setStepReplies({
          replies: values.map((value) => ({
            title: question.id,
            value,
          })) as Reply[],
          id: lineParent.id as PATH,
        }),
      )
    } else {
      // `update_answers` concatène : il faut vider avant de réécrire.
      dispatch(actions.unsetStepReplies({ id: question.id as PATH }))
      dispatch(
        actions.setStepReplies({
          replies: getRepliesForValues(question, values),
          id: question.id as PATH,
        }),
      )
    }

    getStaleFollowupAnswerIds(question, answers, followup?.answerId).forEach(
      (answerId) => dispatch(actions.resetReply(answerId)),
    )

    if (followup) {
      dispatch(actions.resetReply(followup.answerId))
      dispatch(
        actions.setStepReplies({
          replies: getRepliesForValues(followup.question, followupValues),
          id: followup.answerId as PATH,
        }),
      )
    }

    backToRecap()
  }

  const renderReplies = (
    target: Question,
    selected: Value[],
    parent?: QuestionsLine,
  ) => {
    if (parent) {
      return (
        <UiQuestionsLine
          question={{ ...parent, questions: [target] }}
          answers={selected.map((value) => ({ title: target.id, value }))}
          onSelect={(reply) => onChange([reply.value])}
          unSelect={() => undefined}
        />
      )
    }

    if (target.ui?.view === "slider") {
      return (
        <Slider
          items={target.replies}
          label={target.subtitle}
          selectedIndex={target.replies.findIndex((reply) =>
            isValueSelected(selected, reply.value),
          )}
          onSelect={(item) => onChange([item.value])}
        />
      )
    }

    return (
      <UiCarousel
        items={target.replies}
        selected={selected}
        ui={target.ui}
        onSelect={(reply) =>
          onChange(
            target.multipleReplies
              ? [
                  ...selected.filter(
                    (value) => !valueEquals(value, reply.value),
                  ),
                  reply.value,
                ]
              : [reply.value],
          )
        }
        unSelect={(reply) => {
          // Un choix unique ne se dé-sélectionne pas : on en choisit un autre.
          if (target.multipleReplies)
            onChange(
              selected.filter((value) => !valueEquals(value, reply.value)),
            )
        }}
      />
    )
  }

  const isSlider = displayed.ui?.view === "slider"
  const isLastStep = isFollowupStep || !followup
  const cardTitle =
    !isFollowupStep && lineParent ? lineParent.title : displayed.title

  return (
    <>
      <PreviousButton onClick={previous} />
      <Card
        title={cardTitle}
        subtitle={isSlider ? undefined : displayed.subtitle}
        subtitleCustomStyle={displayed.subtitleCustomStyle}
        imgUrl={isFollowupStep || !lineParent ? displayed.imgUrl : undefined}
        doNotAnimateTitle
      >
        <div className={styles.Patch}>
          {renderReplies(
            displayed,
            displayedValues,
            isFollowupStep ? undefined : lineParent,
          )}

          <Buttons>
            <Button
              type="primary"
              disabled={!displayedValues.length}
              onClick={() => (isLastStep ? save() : setOnFollowup(true))}
            >
              {isLastStep ? languages.save : languages.next}
            </Button>
          </Buttons>
        </div>
      </Card>
    </>
  )
}

export default SurveyPatch
